import { Box, Button, Container, Header, Modal, SpaceBetween, Textarea } from "@amzn/awsui-components-react";
import { getString } from "common";
import { useState } from "react";
import { STATUS_OPTIONS } from "interfaces/hardware-status-options";

const stringPath = "orderDetails.tabs.itemsRequested.changeNoteModal";

interface ChangeNoteModalProps {
  showModal: boolean;
  onDismiss: () => void;
  changes?: Record<string, any>;
  columnDefinitions?: any[];
  originalValues?: Record<string, any>;
  username?: string;
  order?: any;
  item?: any;
  onSubmit?: (noteText: string) => void;
}

export const ChangeNoteModal = (props: ChangeNoteModalProps) => {
  const { showModal, onDismiss, changes, columnDefinitions, originalValues, username, order, item, onSubmit } = props;

  const getFieldDisplayName = (fieldId: string) => {
    // First try to find by exact match
    let column = columnDefinitions?.find(col => col.id === fieldId);

    // If not found and it's a nested path, try the last part
    if (!column && fieldId.includes('.')) {
      const lastPart = fieldId.split('.').pop();
      column = columnDefinitions?.find(col => col.id === lastPart);
    }

    return column?.header || fieldId;
  };

  const getStatusLabel = (value: string) => {
    const statusOption = STATUS_OPTIONS
      .flatMap(group => group.options)
      .find(option => option.value === value);
    return statusOption?.label || value;
  };
  const [inputNote, setInputNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const generateChangeText = () => {
    let text = "";
    if (username) {
      text += `${getString(`${stringPath}.changes.by`)} ${username}\n`;
    }
    if (item?.itemInformation?.name) {
      text += `${getString(`${stringPath}.changes.itemName`)} ${item.itemInformation.name}\n\n`;
    }
    if (changes) {
      Object.entries(changes).forEach(([field, newValue]) => {
        text += `${getString(`${stringPath}.changes.fieldUpdated`)} ${getFieldDisplayName(field)}\n`;
        const previousValue = field === 'status' && originalValues?.[field] ? getStatusLabel(originalValues[field]) : (originalValues?.[field] || 'N/A');
        const displayNewValue = field === 'status' ? getStatusLabel(newValue) : newValue;
        text += `${getString(`${stringPath}.changes.previousValue`)} ${previousValue}\n`;
        text += `${getString(`${stringPath}.changes.newValue`)} ${displayNewValue}\n`;
        text += `----------------------------\n\n`;
      });
    }
    text += `${getString(`${stringPath}.changes.justification`)}\n${inputNote}`;
    return text;
  };

  const sendNote = async () => {
    if (!order?.orderId) return;
    setIsLoading(true);
    try {
      const noteText = generateChangeText();
      await onSubmit?.(noteText);
    } catch (error) {
      // Handle error in the main component
    } finally {
      setIsLoading(false);
    }
  };

  // Modal sections
  const modalHeader = (
    <Header
      variant="h2"
    >
      {getString(`${stringPath}.header`)}
    </Header>
  );

  const modalBody = (
    <SpaceBetween
      direction="vertical"
      size="xs">
      <Container>
        {item?.itemInformation?.name && <p><strong>{getString(`${stringPath}.changes.itemName`)}</strong> {item.itemInformation.name}</p>}
        {username && <p><strong>{getString(`${stringPath}.changes.by`)}</strong> {username}</p>}
        {changes && Object.entries(changes).map(([field, newValue], index, array) => (
          <div key={field} style={{ marginBottom: '12px' }}>
            <p style={{ margin: '0 0 4px 0' }}>
              <strong>{getString(`${stringPath}.changes.fieldUpdated`)}</strong> {getFieldDisplayName(field)}
            </p>
            <ul style={{ margin: '0', paddingLeft: '20px', listStyleType: 'disc' }}>
              <li style={{ marginBottom: '2px' }}>
                <strong>{getString(`${stringPath}.changes.previousValue`)}</strong> {String(field === 'status' && originalValues?.[field] ? getStatusLabel(originalValues[field]) : (originalValues?.[field] || 'N/A'))}
              </li>
              <li style={{ marginBottom: '2px' }}>
                <strong>{getString(`${stringPath}.changes.newValue`)}</strong> {String(field === 'status' ? getStatusLabel(newValue) : newValue)}
              </li>
            </ul>
            {index < array.length - 1 && <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '8px 0' }} />}
          </div>
        ))}
      </Container>
      <Header
        variant="h3"
      >
        {getString(`${stringPath}.description`)}
      </Header>
      <Textarea
        data-testid="note-justification-input"
        onChange={(event) => setInputNote(event.detail.value)}
        value={inputNote}
        disabled={isLoading}
      />
    </SpaceBetween>
  );

  const modalFooter = (
    <Box float="right" padding="xxs">
      <SpaceBetween
        direction="horizontal"
        size="xs">
        <Button
          data-testid="cancel-note-button"
          variant="link"
          onClick={onDismiss}
        >
          {getString(`${stringPath}.cancelButton`)}
        </Button>
        <Button
          data-testid="submit-note-button"
          variant="primary"
          onClick={sendNote}
          loading={isLoading}
          disabled={isLoading || !inputNote || inputNote.trim() === ''}
        >
          {getString(`${stringPath}.submitButton`)}
        </Button>
      </SpaceBetween>
    </Box>
  );

  return (
    <Modal
      data-testid="change-note-modal"
      onDismiss={onDismiss}
      visible={showModal}
      size="large"
      header={modalHeader}
      footer={modalFooter}
    >
      {modalBody}
    </Modal>
  );
}
